import { useCallback, useMemo, useState } from 'react';
import {
  computeOverall,
  MAX_AROMA_TAGS,
  upsertTastingSchema,
  type UpsertTastingInput,
} from '@cercle/shared';

export interface TastingFormState {
  nose: number;
  palate: number;
  finish: number;
  comment: string;
  aromaTags: string[];
}

const EMPTY: TastingFormState = { nose: 50, palate: 50, finish: 50, comment: '', aromaTags: [] };

/**
 * Formulaire de note (US-08 : « saisie complète en moins de 60 secondes sur mobile »).
 *
 * Validé par le **même** schéma zod que l'API : un champ qui passe ici passera là-bas, et
 * un descripteur inconnu est refusé des deux côtés sans qu'on ait à maintenir deux listes.
 *
 * La note globale n'est pas saisie : elle se recalcule à chaque mouvement de curseur, ce
 * que la maquette montre en gros au-dessus des trois critères.
 */
export function useTastingForm(initial?: Partial<TastingFormState>) {
  const [state, setState] = useState<TastingFormState>({ ...EMPTY, ...initial });
  const [error, setError] = useState<string | null>(null);

  const overall = useMemo(
    () => computeOverall(state.nose, state.palate, state.finish),
    [state.nose, state.palate, state.finish],
  );

  const setCriterion = useCallback((key: 'nose' | 'palate' | 'finish', value: number) => {
    setState((current) => ({ ...current, [key]: value }));
  }, []);

  const setComment = useCallback((comment: string) => {
    setState((current) => ({ ...current, comment }));
  }, []);

  const toggleTag = useCallback((tag: string) => {
    setState((current) => {
      if (current.aromaTags.includes(tag)) {
        return { ...current, aromaTags: current.aromaTags.filter((t) => t !== tag) };
      }
      // Au-delà de la limite, on ignore silencieusement plutôt que d'afficher une erreur :
      // les descripteurs sont déjà visuellement désactivés.
      if (current.aromaTags.length >= MAX_AROMA_TAGS) return current;
      return { ...current, aromaTags: [...current.aromaTags, tag] };
    });
  }, []);

  const reset = useCallback((next?: Partial<TastingFormState>) => {
    setState({ ...EMPTY, ...next });
    setError(null);
  }, []);

  /** Construit et valide le payload. Renvoie `null` et pose l'erreur si la saisie est invalide. */
  const build = useCallback(
    (context: { sessionId: string; rumId: string; validate: boolean }): UpsertTastingInput | null => {
      const candidate = {
        sessionId: context.sessionId,
        rumId: context.rumId,
        nose: state.nose,
        palate: state.palate,
        finish: state.finish,
        comment: state.comment.trim() || null,
        aromaTags: state.aromaTags,
        validate: context.validate,
        // Horodatage de la saisie sur l'appareil : c'est lui qui arbitre le last-write-wins
        // quand la note part de la file offline plusieurs minutes plus tard.
        clientUpdatedAt: new Date().toISOString(),
      };

      const parsed = upsertTastingSchema.safeParse(candidate);
      if (!parsed.success) {
        setError(parsed.error.issues[0]?.message ?? 'Note invalide.');
        return null;
      }
      setError(null);
      return parsed.data;
    },
    [state],
  );

  return {
    state,
    overall,
    error,
    tagsRemaining: MAX_AROMA_TAGS - state.aromaTags.length,
    setCriterion,
    setComment,
    toggleTag,
    reset,
    build,
  };
}
